import type { WizardSnapshot } from "./wizard-types";

/**
 * localStorage persistence for the Quick Setup wizard so a refresh / accidental
 * tab close never loses a seller's progress. Keyed per store. Image data-URLs
 * (logo + product photos) can be large, so saving degrades gracefully: if the
 * full snapshot blows the storage quota we retry with the heavy image data
 * stripped, preserving at least the textual progress.
 */

const VERSION = 2;
const keyFor = (storeSlug: string) => `a2z:quick-setup:v${VERSION}:${storeSlug}`;

export function loadSnapshot(storeSlug: string): WizardSnapshot | null {
  if (typeof window === "undefined" || !storeSlug) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(storeSlug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WizardSnapshot;
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSnapshot(storeSlug: string, snapshot: WizardSnapshot): void {
  if (typeof window === "undefined" || !storeSlug) return;
  const key = keyFor(storeSlug);
  try {
    window.localStorage.setItem(key, JSON.stringify(snapshot));
  } catch {
    // Quota exceeded — most likely the data-URL images. Retry with a lighter
    // snapshot so the seller still resumes their text answers and progress.
    try {
      const lite: WizardSnapshot = {
        ...snapshot,
        logo: null,
      };
      window.localStorage.setItem(key, JSON.stringify(lite));
    } catch {
      // Give up silently — persistence is best-effort, never blocks the wizard.
    }
  }
}

export function clearSnapshot(storeSlug: string): void {
  if (typeof window === "undefined" || !storeSlug) return;
  try {
    window.localStorage.removeItem(keyFor(storeSlug));
  } catch {
    // ignore
  }
}
